import React,{useEffect, useState} from 'react'
import { connect, useSelector } from 'react-redux'
import ReactDatePicker from 'react-datepicker'
import { Button, Col, Container, DropdownItem, DropdownMenu, DropdownToggle, Input, InputGroup, Row, UncontrolledDropdown } from 'reactstrap'
import { setUnsavedChanges } from 'store/actions'
import axios from 'axios'
import Cookies from 'js-cookie'
import { PERSONAL_INFO_FIELDS as fields } from 'consts'
import { fixNumAndRemoveDecimalZero } from 'utils'
import { getAllCountries, getRefreshedUserData } from 'api/api-manager'
import moment from 'moment'
import PencilIcon from '../../../assets/img/icons/Edit.svg'
import PhoneNumberInput from 'components/Inputs/phone-number-input/phone-number-input'
import { t } from 'multi-language/multi-lang.service'

const MAX_CHARS_LIMITS = 24

export function PersonalInfo({user,setUser,unsavedChanges,setUnsavedChanges,jwtToken, role}) {

    const countries = useSelector(state => state.countries)
    const [data, setData] = useState(user)
    const [isOpen, setIsOpen] = useState(false)
    const [inputError, setInputError] = useState(null)
    const [saveError, setSaveError] = useState()
    const isDoctor = role === 'doctor'

    useEffect( () => {
        // i think this function is unnecessary but it was asked by Menni - it will reload patient data because maybe the doctor changed patients data 
        getRefreshedUserData()
        getAllCountries()
    },[])

    useEffect(() => {
        if(!countries.length) return
        setData({...data, country: getCountryNameByCode(user.country)})
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [countries])

    useEffect( () => {
        if(inputError === null) return
        (inputError && inputError !== '') ? setUnsavedChanges(null,false) : setUnsavedChanges(handleSave,true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    },[data])

    const getCountryCodeByName = (countryName) => {
        const countryCode = countries?.find(c => c.name.toLowerCase() === countryName.toLowerCase()).countryCode
        return countryCode
    }

    const getCountryNameByCode = (countryCode) => {
        const countryName = countries?.find(c => c.countryCode === countryCode)?.name
        return countryName
    }
    
    const handleSave = ()=>{
        const countryCode = getCountryCodeByName(data.country)
        const newData = {
            ...data,
            patientId: data.id,
            weight: Number(data.weight),
            height: Number(data.height),
            birthDate: moment(data.dateOfBirth).format('YYYY-MM-DD'),
            country: countryCode
        }
        if(data['timeZone'] || data['timeZone'] === 0) newData.timeZone = Number(data['timeZone'])
        axios.post(`${process.env.REACT_APP_API_URL}/app/patient/update-patient-information`,
            newData,{ headers: {'Authorization': `Bearer ${jwtToken}` } })
            .then(()=>{
                const updatedUser = Object.assign({},user,newData)
                setUser({data:updatedUser,type:'patient', jwtToken})
                Cookies.set('user', JSON.stringify({data:updatedUser,type:'patient', jwtToken}), {expires:1/24})
                setUnsavedChanges()
            })
            .catch(err=>{
                const {data} = err.response
                const {message} = data
                setSaveError(message)
            })  
    }

    const getSelectOptions = (action) => {
        let {options} = fields[action]
        if(action === 'country'){
            options = countries?.map(c => c.name)
        }
        return options?.map(op => {
            const key = `${op}`.toLowerCase()
            return <DropdownItem key={op} active={key == data[action]} onClick={() => handleChange(key, action)}>{op}</DropdownItem>
        })
    }

    const isEditble = (field) => {
        return !isDoctor && field.editble
    }

    /**
     * This function only has to perform client side validation. Actual
     * data conversions take place in the server.
     */
    const handleChange = (value,key) => {
        if(key==="dateOfBirth") setIsOpen(!isOpen);
        setInputError('')
        if (key === 'weight') {
            let errMsg = ''
            if (user.weightUnit === 'lb') {
                if (value > 550) {errMsg = `${t('pages.personal_data.high_warning')} 550`}
                if (value < 88)  {errMsg = `${t('pages.personal_data.low_warning')} 88`}
            } else {
                if (value > 250) {errMsg = `${t('pages.personal_data.high_warning')} 250`}
                if (value < 40)  {errMsg = `${t('pages.personal_data.low_warning')} 40`}
            }

            setData(prevData => ({...prevData,[key]:value}))
            setInputError(errMsg)
            return
        }

        if (key === 'height') {
            let errMsg = ''
            if (user.heightUnit === 'ft') {
                if (value > 7.2) {errMsg = `${t('pages.personal_data.high_warning')} 7.2`}
                if (value < 3.2)  {errMsg = `${t('pages.personal_data.low_warning')} 3.2`}
            } else {
                if (value > 220) {errMsg = `${t('pages.personal_data.high_warning')} 220`}
                if (value < 100) {errMsg = `${t('pages.personal_data.low_warning')} 100`}
            }

            setData(prevData => ({...prevData,[key]:value}))
            setInputError(errMsg)
            return
        }

        if (key!=="dateOfBirth" && String(value).length>MAX_CHARS_LIMITS){
            setInputError(t('pages.personal_data.length_warning'))
            return
        } 
        setData(prevData => ({...prevData,[key]:value}))
    }

    const handleEditButtonClick = (action,e) => {
        e.preventDefault();
        inputError && setInputError()
        action ==="dateOfBirth" && setIsOpen(!isOpen);
    }

    const renderField = (key)=>{
        if(key==="dateOfBirth"){
            return new Date(data[key]).toLocaleDateString('en-GB')
        }
        if(key==="weight"){
            return `${fixNumAndRemoveDecimalZero(data[key], 1)} ${user.weightUnit}`
        }
        if(key==="height"){
            return `${fixNumAndRemoveDecimalZero(data[key], 1)} ${user.heightUnit}`
        }
        return data[key]
    }

    const editButton = (action) => {
        if(!isEditble(fields[action])) return null

        return (
            <UncontrolledDropdown>

                <DropdownToggle style={{padding:0, boxShadow:'none',backgroundColor:'transparent'}} onClick={e=>handleEditButtonClick(action,e)} >
                    <img alt="edit" src={PencilIcon} width="25px" />
                </DropdownToggle>

                {action !== "dateOfBirth" && 
                    <DropdownMenu boundary='scrollParent' className={`dropdown-menu-arrow ${action === 'gender' ? 'mt-0' : 'mt-7'}`} right>
                        {
                            fields[action].type === "select" ?
                                getSelectOptions(action )
                            :
                            action === 'phoneNumber' ?
                                <PhoneNumberInput phoneNumber={data.phoneNumber} setPhoneNumber={(val) => handleChange(val, 'phoneNumber')} countryCode={user.country} />
                            :
                            (
                                <InputGroup className="input-group-alternative">
                                    <div className='input-group-append'>
                                        <div className='input-group-text'>
                                            <i className="fas fa-save"/>
                                        </div>
                                    </div>
                                    <Input
                                        id='personal-input-edit'
                                        type={fields[action].type} 
                                        value={data[action]}
                                        onChange={e=>handleChange(e.target.value,action)} />
                                </InputGroup>
                            )
                        }
                        <small style={{color:'#EF3054',padding:'2px 5px'}}>{inputError}</small>
                        
                    </DropdownMenu>
                }

            {isOpen && action ==="dateOfBirth" && 
            <div className="info-date-picker">
                <ReactDatePicker
                    id='date-of-birth-picker'
                    selected={new Date(data["dateOfBirth"])}
                    onChange={val=>handleChange(val,action)} 
                    filterDate={date=>date<=new Date(new Date().getTime()-18*365*24*60*60*1000)}
                    showYearDropdown
                    dropdownMode="select"
                    inline />
            </div> }

        </UncontrolledDropdown> 
       )
    }
            
    return (
        <Container id='personal-info-page' fluid>
            <div className="profile-content-container">
                <div style={{marginLeft:'55px',marginTop:'40px',marginBottom:'40px'}} >
                    {!isDoctor && <div style={{fontSize:'large',paddingBottom:'10px',color:'#053f7f'}} >{t('pages.personal_data.edit')}</div>}
                    {Object.keys(fields).map(key=>{
                        return(
                            <Row key={key} className={`personal-info-row ${isEditble(fields[key]) ? 'row-editble' : '' }`}>
                                <Col style={{color:'#000',fontWeight:200}} > {t(fields[key].translationPath)} </Col>
                                <Col className={fields[key].type === 'select' ? 'text-capitalize' : ''}> {renderField(key)} </Col>
                                <Col xl="1" > 
                                    {editButton(key)}
                                </Col>
                            </Row>
                        )
                    })}
                    <div style={{display:'flex',width:'50%',marginTop:'20px'}}>
                        <small style={{color:'#EF3054',padding:'2px 5px'}}>{saveError?.join(', ')}</small>
                        <Button className="text-white"
                                onClick={handleSave} 
                                style={{display:unsavedChanges?'block':'none',padding:'0.425rem 1.05rem',backgroundColor:'#3873AE',marginLeft:'auto'}}>
                            {t('general_words.save')}
                        </Button>
                    </div>
                </div>
                
            </div>
        </Container>
    )
}


const mapStateToProps = ({unsavedChanges}) => ({ unsavedChanges })
const mapDispatchToProps = { setUnsavedChanges }
export default connect(mapStateToProps, mapDispatchToProps)(PersonalInfo)
